import type { FC } from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

/** True while a client-side page change is loading. */
export const useRouteLoading = (): boolean => {
    const router = useRouter()
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const start = (url: string, { shallow }: { shallow: boolean }) => {
            if (!shallow) setLoading(true)
        }
        const done = () => setLoading(false)

        router.events.on('routeChangeStart', start)
        router.events.on('routeChangeComplete', done)
        router.events.on('routeChangeError', done)

        return () => {
            router.events.off('routeChangeStart', start)
            router.events.off('routeChangeComplete', done)
            router.events.off('routeChangeError', done)
        }
    }, [router])

    return loading
}

/** Thin bar at the top of the window during page changes. Waits briefly so fast loads show nothing. */
export const RouteProgress: FC = () => {
    const loading = useRouteLoading()
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        if (!loading) {
            setVisible(false)
            return
        }

        const timer = setTimeout(() => setVisible(true), 150)
        return () => clearTimeout(timer)
    }, [loading])

    if (!visible) return null

    return (
        <div
            role='progressbar'
            aria-label='Loading page'
            className='pointer-events-none fixed inset-x-0 top-0 z-[60] h-0.5 overflow-hidden bg-primary/15'
        >
            <div className='route-progress-bar h-full w-full bg-primary' />
        </div>
    )
}
